import "../sellscreendialogs.scss";
import {
    type FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { StatusCheck, type StatusCheckResult, TouchNumbers } from "@onshopfront/components";
import { useShopfrontDispatch, useShopfrontSelector } from "#/_Bootstrap/hooks.ts";
import { hideDialog } from "#/Actions/Dialogs/DialogActions.ts";
import {
    type GiftCardPayment,
    requestPayGiftCard,
    requestSelectGiftCard,
    type SaleAddPaymentOptions,
} from "#/Actions/SellScreen/SellScreenPaymentActions.tsx";
import { getPolicy } from "#/Utilities/Platform/Policies/Policies.ts";
import Button from "../../../../Common/Buttons/Button";
import { getExistingGiftCards } from "./RequestCode";

export const RequestGiftCard: FC<{
    method: string;
    amount: number;
    options: SaleAddPaymentOptions;
}> = ({
    method,
    amount,
    options,
}) => {
    const [ value, setValue ] = useState("");
    const [ found, setFound ] = useState<false | GiftCardPayment>(false);
    const [ multipleFound, setMultipleFound ] = useState<Array<GiftCardPayment>>([]);
    const [ source, setSource ] = useState("internal");
    const dispatch = useShopfrontDispatch();
    const inputRef = useRef<HTMLInputElement | null>(null);

    // addCard() needs the result of the most recent check the instant it settles (e.g. when triggered
    // automatically after pressing Enter mid-check), which can be a render ahead of `found`/`multipleFound`
    // React state - so it reads from these refs (kept in sync alongside the state) instead.
    const foundRef = useRef<false | GiftCardPayment>(false);
    const multipleFoundRef = useRef<Array<GiftCardPayment>>([]);

    const submitRef = useRef({
        canSubmit        : false,
        submitWhenChecked: false,
        checking         : false,
    });

    const outlet = useShopfrontSelector(state => state.location.outlet ? state.location.outlet.id : null);
    const payments = useShopfrontSelector(state => state.sellscreen.sale.payments);

    const saleCodes = useMemo(() => {
        return payments.flatMap(payment => {
            if(payment.type !== "giftcard" || payment.status !== "completed" || payment.reverted) {
                return [];
            }

            try {
                const metaData = JSON.parse(payment.metadata);

                if(typeof metaData.gift_card !== "object" || metaData.gift_card === null) {
                    return [];
                }

                if(typeof metaData.gift_card.id === "string") {
                    return [ metaData.gift_card.id ];
                }
            } catch {
                // Ignore
            }

            return [];
        });
    }, [ payments ]);

    useEffect(() => {
        if(options.source === "auto" || options.source === "internal") {
            setSource("internal");
        } else {
            const sources = getPolicy("gift_card_sources", []);

            for(let i = 0, l = sources.length; i < l; i++) {
                if(sources[i].connection === options.source) {
                    setSource(sources[i].address);

                    return;
                }
            }

            throw new Error("Unable to find gift card source address");
        }
    }, [ options.source ]);

    const addCard = useCallback(() => {
        const found = foundRef.current;
        const multipleFound = multipleFoundRef.current;

        if(!found) {
            return;
        }

        if(multipleFound.length) {
            dispatch(requestSelectGiftCard(
                value,
                method,
                amount,
                options,
                multipleFound
            ));
        } else {
            dispatch(requestPayGiftCard(
                method,
                amount,
                {
                    ...options,
                    giftcard: found,
                }
            ));
        }
    }, [ amount, dispatch, method, options, value ]);

    const handleValueChange = useCallback((newValue: string) => {
        submitRef.current.checking = true;
        submitRef.current.canSubmit = false;
        setValue(newValue);
        setFound(false);
        setMultipleFound([]);
        foundRef.current = false;
        multipleFoundRef.current = [];
    }, []);

    // Guards against a slower, superseded check (e.g. a stalled network request) overwriting state with stale
    // results after a newer check for a later value has already completed.
    const checkIdRef = useRef(0);

    const checkCodeStatus = useCallback(async (code: string): Promise<StatusCheckResult> => {
        const checkId = ++checkIdRef.current;

        submitRef.current.checking = true;
        submitRef.current.canSubmit = false;

        let result: StatusCheckResult;
        let foundResult: false | GiftCardPayment = false;
        let multipleResult: Array<GiftCardPayment> = [];

        if(!code) {
            result = null;
        } else {
            try {
                const codes = await getExistingGiftCards(source, code, outlet, options.source, false);
                const filtered = codes.filter(giftCard => !saleCodes.includes(giftCard.id));

                if(filtered.length > 0) {
                    foundResult = filtered[0];
                }

                if(filtered.length > 1) {
                    multipleResult = filtered;
                }

                result = foundResult ? true : [ false, "Gift Card not found" ];
            } catch(err) {
                console.error("Getting gift cards by code failed", err);

                result = [
                    null,
                    "Unable to verify this code, please try again",
                    [ "far", "triangle-exclamation" ],
                    "text-orange-500",
                ];
            }
        }

        // A newer check has since started - its state has already superseded this one, so don't touch anything.
        if(checkId !== checkIdRef.current) {
            return result;
        }

        foundRef.current = foundResult;
        multipleFoundRef.current = multipleResult;
        setFound(foundResult);
        setMultipleFound(multipleResult);

        submitRef.current.canSubmit = !!foundResult;
        submitRef.current.checking = false;

        if(submitRef.current.submitWhenChecked) {
            submitRef.current.submitWhenChecked = false;

            if(!submitRef.current.canSubmit) {
                inputRef.current?.select();
            }

            addCard();
        }

        return result;
    }, [ addCard, options.source, outlet, saleCodes, source ]);

    let multi = null;

    if(multipleFound.length) {
        multi = (
            <span className="multiple-gift-cards">Multiple gift cards found</span>
        );
    }

    return (
        <div className="request-gift-card">
            <p>Please enter or scan the gift card&apos;s code</p>
            <StatusCheck
                value={value}
                status={checkCodeStatus}
                position="right-2 top-3"
            >
                {hasIcon => (
                    <TouchNumbers
                        ref={inputRef}
                        mode="string"
                        value={value}
                        onChange={handleValueChange}
                        autoFocus
                        selectOnAutoFocus
                        className={hasIcon ? "pr-8" : undefined}
                        onKeyDown={e => {
                            if(e.key === "Enter") {
                                if(submitRef.current.canSubmit) {
                                    addCard();
                                } else if(submitRef.current.checking) {
                                    submitRef.current.submitWhenChecked = true;
                                }

                                e.preventDefault();
                                e.stopPropagation();
                            }
                        }}
                    />
                )}
            </StatusCheck>
            {multi}
            <div className="buttons">
                <Button
                    type="button"
                    classes={[ "button-cancel" ]}
                    onClick={() => dispatch(hideDialog())}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    classes={[ "button-save" ]}
                    onClick={addCard}
                    disabled={!found}
                >
                    Add
                </Button>
            </div>
        </div>
    );
};
